/**
 * RevenueCat Capacitor SDK wrapper.
 *
 * All exported functions are safe to call in any environment:
 *   - On a native Capacitor platform (iOS), they call into the real SDK.
 *   - In the browser (Vite dev, deployed web build), they no-op and log a
 *     warning. This lets us wire RevenueCat into the app and verify the
 *     call sequence in DevTools before an iOS build exists.
 *
 * Public API:
 *   isNativePlatform()             → boolean
 *   initRevenueCat()               → Promise<void>
 *   identifyUser(uid)              → Promise<void>
 *   fetchOfferings()               → Promise<Package[] | null>
 *   purchasePackage(identifier)    → Promise<{ status, ... }>
 *
 * purchasePackage resolves with one of:
 *   { status: 'success',   customerInfo }
 *   { status: 'cancelled' }
 *   { status: 'error',     error }
 *   { status: 'skipped',   reason }   ← non-native or no offering
 */
import { Capacitor } from '@capacitor/core'
import { Purchases, PURCHASES_ERROR_CODE } from '@revenuecat/purchases-capacitor'

// Dev-only logger — the "skipped — not native platform" messages fire on every
// browser interaction (normal, expected behavior). Logging them as warnings in
// production made benign browser behavior look like an error. Gate behind DEV
// and use console.log so they're informational and silent in production.
// Genuine failures still use console.error directly.
const devLog = (...args) => { if (import.meta.env.DEV) console.log(...args) }

export function isNativePlatform() {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

export async function initRevenueCat() {
  if (!isNativePlatform()) {
    devLog('[RC] init skipped — not native platform')
    return
  }
  const apiKey = import.meta.env.VITE_REVENUECAT_APPLE_KEY
  if (!apiKey || apiKey.startsWith('appl_PLACEHOLDER')) {
    console.warn('[RC] init skipped — VITE_REVENUECAT_APPLE_KEY is missing or still the placeholder')
    return
  }
  try {
    await Purchases.configure({ apiKey })
    console.log('[RC] init complete')
  } catch (err) {
    console.error('[RC] init failed:', err)
  }
}

export async function identifyUser(uid) {
  if (!isNativePlatform()) {
    devLog('[RC] identifyUser skipped — not native platform')
    return
  }
  if (!uid) {
    console.warn('[RC] identifyUser skipped — no uid')
    return
  }
  try {
    await Purchases.logIn({ appUserID: uid })
    console.log(`[RC] user identified: ${uid}`)
  } catch (err) {
    console.error('[RC] identifyUser failed:', err)
  }
}

export async function fetchOfferings() {
  if (!isNativePlatform()) {
    devLog('[RC] fetchOfferings skipped — not native platform')
    return null
  }
  try {
    const offerings = await Purchases.getOfferings()
    const current   = offerings?.current
    const packages  = current?.availablePackages ?? []
    console.log(`[RC] offerings fetched: ${packages.length} packages`)
    return packages
  } catch (err) {
    console.error('[RC] fetchOfferings failed:', err)
    return null
  }
}

export async function purchasePackage(identifier) {
  if (!isNativePlatform()) {
    devLog(`[RC] purchasePackage skipped — not native platform (identifier=${identifier})`)
    return { status: 'skipped', reason: 'not-native' }
  }
  console.log(`[RC] purchase started: ${identifier}`)
  try {
    const offerings = await Purchases.getOfferings()
    const current   = offerings?.current
    const pkg       = current?.availablePackages?.find((p) => p.identifier === identifier)
    if (!pkg) {
      console.error(`[RC] purchase aborted — package not found in current offering: ${identifier}`)
      return { status: 'error', error: new Error(`Package not found: ${identifier}`) }
    }
    const result = await Purchases.purchasePackage({ aPackage: pkg })
    console.log(`[RC] purchase result: success ${identifier}`)
    return { status: 'success', customerInfo: result?.customerInfo, identifier }
  } catch (err) {
    // RevenueCat surfaces user-cancellation via a specific error code
    const code = err?.code ?? err?.userCancelled
    const cancelled =
      code === PURCHASES_ERROR_CODE?.PURCHASE_CANCELLED_ERROR ||
      err?.userCancelled === true
    if (cancelled) {
      console.log(`[RC] purchase result: cancelled ${identifier}`)
      return { status: 'cancelled', identifier }
    }
    console.error(`[RC] purchase result: error ${identifier}`, err)
    return { status: 'error', error: err, identifier }
  }
}
